import { useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, call } from '@/api/client';
import { qk } from '@/api/keys';
import { isApiError, messageOf } from '@/api/errors';
import { toast } from '@/components/ui';
import type { SubscriptionStatusOut } from '@/api/schema';

/**
 * Web checkout through Stripe (web_app_design.md §2). The server decides everything that matters —
 * which account, which price, whether a trial applies, whether a second subscription would be
 * opened — so these hooks only ask for a Stripe page and send the browser to it.
 */

/**
 * The paywall's catalogue, priced where the viewer is rather than by their sign-up country: Stripe
 * Checkout charges by IP, so this is the price Checkout will ask for (`services/billing/plans.py`
 * `region_for_location`). Every reader of `qk.plans` on the web uses this, so the cache holds one
 * answer.
 */
export const plansQuery = {
  queryKey: qk.plans,
  queryFn: () => call(api.GET('/api/v1/plans', { params: { query: { priced_by: 'location' } } })),
};

export const STORE_NAME: Record<string, string> = { app_store: 'the App Store', play_store: 'Google Play' };

/**
 * Who bills this account now, for the copy and for which buttons show. Mirrors the server's
 * refusal (`services/billing/web_checkout.py`): a web subscription is managed in the portal, a
 * store one that still renews is managed in its store, and anything else may subscribe here.
 */
export function billedBy(sub: SubscriptionStatusOut | undefined): 'web' | 'store' | null {
  if (!sub || sub.source === 'free' || !sub.billing_provider) return null;
  if (sub.billing_provider === 'stripe') return 'web';
  // Any provider still charging, across every live subscription — not only the best one's
  // (STR-10): a cancelled App Store Elite can sit above a Stripe Pro that still renews, and the
  // page would otherwise offer a checkout the server refuses. Older servers lack the field, so the
  // best subscription's own renewal is the fallback.
  const renewing = sub.renewing_provider ?? (sub.auto_renew ? sub.billing_provider : null);
  if (renewing === 'stripe') return 'web';
  return renewing ? 'store' : null;
}

function go(url: string) {
  window.location.assign(url);
}

export function useStartCheckout() {
  return useMutation({
    mutationFn: (productId: string) =>
      call(api.POST('/api/v1/subscriptions/checkout', { body: { product_id: productId } })),
    onSuccess: (out) => go(out.url),
    onError: (e) => toast.error(messageOf(e)),
  });
}

export function useOpenPortal() {
  return useMutation({
    mutationFn: () => call(api.POST('/api/v1/subscriptions/portal')),
    onSuccess: (out) => go(out.url),
    onError: (e) => toast.error(messageOf(e)),
  });
}

/**
 * Stripe sends the browser back to `/plans?checkout=done&session_id=…` (or `checkout=cancelled`).
 * Confirming grants the plan at once rather than waiting for the webhook; the parameters are then
 * removed so a reload does not confirm again. Read from `window.location` rather than the router
 * so the page renders the same without one.
 */
export function useCheckoutReturn() {
  const qc = useQueryClient();
  const handled = useRef(false);
  const confirm = useMutation({
    mutationFn: (sessionId: string) =>
      call(api.POST('/api/v1/subscriptions/checkout/confirm', { body: { session_id: sessionId } })),
    onSuccess: (status) => toast.success(`You're on ${status.plan_name}. Thanks for subscribing.`),
    // A refusal (4xx: an unfinished session, another account's, a malformed id) is a real "no";
    // only an outage (5xx, network) means the webhook will grant it anyway and this merely
    // failed to say so on time.
    onError: (e) => {
      if (isApiError(e) && e.code === 'subscription_ownership_conflict') {
        // Paid while a different account was signed in here. The purchase is safe — the webhook
        // puts it on the account that made it — so this is directions, not an alarm.
        toast.error(
          "This subscription was bought on a different SixOne8 account. Sign out and sign in with that account to use it — you won't be charged twice.",
        );
      } else if (isApiError(e) && e.status < 500) {
        toast.error("This checkout couldn't be confirmed. If you were charged, contact support with your receipt.");
      } else {
        toast.info('Payment received. Your plan can take a minute to appear — refresh shortly.');
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: qk.subscription });
      void qc.invalidateQueries({ queryKey: qk.plans });
      void qc.invalidateQueries({ queryKey: qk.me });
    },
  });

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;
    const params = new URLSearchParams(window.location.search);
    const outcome = params.get('checkout');
    if (!outcome) return;
    const sessionId = params.get('session_id');
    params.delete('checkout');
    params.delete('session_id');
    const rest = params.toString();
    window.history.replaceState(window.history.state, '', window.location.pathname + (rest ? `?${rest}` : ''));
    if (outcome === 'done' && sessionId) confirm.mutate(sessionId);
    else if (outcome === 'cancelled') toast.info('Checkout cancelled. Nothing was charged.');
  }, [confirm]);

  return confirm;
}
